/**
 * The Line's state on the web, framework-free (spec 2026-09-12 §9). The face
 * IS the state: faceMoodFor maps a snapshot onto the shipped FaceMood
 * vocabulary and toneFor onto the one-light law (gold is you, copper is Bash,
 * every working state is the ball's own gold). The step (thinking, search,
 * read, create, nami, other) is what the glass shows one glyph for; the running
 * task keeps its mark; the idle mirror keeps the server's own two constants
 * against the web's clock so "Still there?" lands in the row when the server
 * asks it in Bash's voice. No text ever derives from here but the closed note.
 */
import type { LiveLineState, RouterEvent, ToolName } from "@bash/shared";
import type { FaceMood } from "@/components/bash-home/BashFace";

export type LinePhase = "off" | "connecting" | "open" | "closing";
/** What the glass shows: thinking has no glyph, the face carries it alone. */
export type LineStep =
  "thinking" | "search" | "read" | "create" | "nami" | "other";

/** The server's idle policy (line-core.ts): 60 s of nothing said and nothing
 *  in flight, then the ask; 10 s more, then the goodbye. Tuning these tunes
 *  both sides. */
export const IDLE_ASK_MS = 60_000;
export const IDLE_GOODBYE_MS = 10_000;

export interface LineSnapshot {
  phase: LinePhase;
  sessionId: string | null;
  /** When this open began connecting (the click), so a surface can tell a
   *  double click from a change of mind. The caller's clock; null if unstamped. */
  connectingAt: number | null;
  startedAt: number | null;
  seconds: number;
  youOn: boolean;
  bashOn: boolean;
  /** The current step of a delegated turn; null between turns. */
  step: LineStep | null;
  /** Bumped on every delegation and every tool_started, so the same tool twice
   *  still re-enters the glyph. */
  stepSeq: number;
  /** A task born by this line is still in flight (the run mark). */
  taskRunning: boolean;
  runningTaskId: string | null;
  /** Bumped per task birth: the spark, the flash and the face pop key on it. */
  births: number;
  /** Set after a task receipt lands (nod, then the check); cleared on the next you/bash/delegation. */
  justDone: boolean;
  /** The idle mirror's verdict: "Still there?" is in the row. */
  idleAsk: boolean;
  /** The last moment a voice was on or a step was in flight, on the tick's clock. */
  lastActiveAt: number | null;
  closedNote: string | null;
  error: string | null;
  /** The moment the note or the error was set, on the caller's clock. The
   *  stage shows a note for its beat from this moment, so a stage mounted
   *  later (back from the type door) never replays a past line's goodbye. */
  noteAt: number | null;
  reconnecting: boolean;
}

export type LineInput =
  | { type: "open"; at?: number }
  | { type: "started"; sessionId: string; at: number }
  | {
      type: "server_state";
      state: LiveLineState;
      reason?: string;
      seconds: number;
      at: number;
    }
  /** A voice edge. The driver sends no clock; an on edge without one stamps
   *  activity with the last tick's second, so an utterance shorter than a
   *  tick still counts against the idle mirror. */
  | { type: "you"; on: boolean; at?: number }
  | { type: "bash"; on: boolean; at?: number }
  | { type: "delegation" }
  | { type: "router"; event: RouterEvent }
  | { type: "close" }
  | { type: "failed"; message: string; at?: number }
  | { type: "reconnecting"; on: boolean }
  | { type: "tick"; at: number }
  /** The Dock, when the running task's dot settles (done, stopped, failed, out of credits). */
  | { type: "task_settled" }
  /** The idle mirror's verdict, dispatched by the hook off the ticker. */
  | { type: "idle_ask"; on: boolean };

export const INITIAL_LINE: LineSnapshot = {
  phase: "off",
  sessionId: null,
  connectingAt: null,
  startedAt: null,
  seconds: 0,
  youOn: false,
  bashOn: false,
  step: null,
  stepSeq: 0,
  taskRunning: false,
  runningTaskId: null,
  births: 0,
  justDone: false,
  idleAsk: false,
  lastActiveAt: null,
  closedNote: null,
  error: null,
  noteAt: null,
  reconnecting: false,
};

const SEARCH: ReadonlySet<ToolName> = new Set<ToolName>([
  "web_search",
  "fetch_recent_tasks",
  "show_tasks",
  "os_find",
  "os_apps",
]);
const READ: ReadonlySet<ToolName> = new Set<ToolName>([
  "web_fetch",
  "task_output",
  "task_transcript",
  "task_state",
  "task_creations",
  "os_snapshot",
  "os_look",
  "os_read",
  "os_selection",
  "os_pointer",
  "os_ocr",
]);
const CREATE: ReadonlySet<ToolName> = new Set<ToolName>([
  "create_task",
  "create_computer_task",
  "control_computer",
  "follow_up_task",
]);
/** The memory itself is the subject (spec 2026-09-12 §2): reading Nami,
 *  reading the person's own thread and writing to Nami are one verb, not a
 *  search and not a create. */
const NAMI: ReadonlySet<ToolName> = new Set<ToolName>([
  "recall_from_nami",
  "recall_from_thread",
  "save_to_nami",
]);

/** The step a tool is (spec §6, the table); everything else is other. A card run (2a, §7) reads as
 *  a read and writes as a create, by the effect the event carries. */
export function stepFor(
  tool: ToolName,
  target?: { effect: string }
): Exclude<LineStep, "thinking"> {
  if (tool === "run_tool" && target)
    return target.effect === "read" ? "read" : "create";
  if (NAMI.has(tool)) return "nami";
  if (SEARCH.has(tool)) return "search";
  if (READ.has(tool)) return "read";
  if (CREATE.has(tool)) return "create";
  return "other";
}

/** The last clock the machine saw: the last tick's second since the start
 *  (seconds is the tick's floor), or the start itself. Null off the line. */
const lastClock = (s: LineSnapshot): number | null =>
  s.startedAt === null ? null : s.startedAt + s.seconds * 1000;

/** The activity stamp an edge leaves: its own clock if it carries one, else
 *  the last clock; never earlier than the stamp already held. */
function stamped(s: LineSnapshot, at?: number): number | null {
  const clock = at ?? lastClock(s);
  if (clock === null) return s.lastActiveAt;
  return s.lastActiveAt === null ? clock : Math.max(s.lastActiveAt, clock);
}

export function mmss(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function closedNoteFor(seconds: number): string {
  return `We talked for ${mmss(seconds)}. Here when you need me.`;
}

const noTask = { taskRunning: false, runningTaskId: null } as const;
/** What a closing line drops: the running mark, the question, and the step,
 *  so no glyph outlives the line in the glass. */
const closingDrops = { ...noTask, idleAsk: false, step: null } as const;

export function reduceLine(s: LineSnapshot, i: LineInput): LineSnapshot {
  switch (i.type) {
    case "open":
      if (s.phase !== "off") return s;
      return {
        ...INITIAL_LINE,
        phase: "connecting",
        connectingAt: i.at ?? null,
      };
    case "started":
      // Only a connecting line opens. A started that lands after the person
      // already closed (a double click: the channel's session.started beats
      // the server's session.closed) must not flip the ball back to open, or
      // the close net finds no closing to finish and the mic stays live.
      if (s.phase !== "connecting") return s;
      return {
        ...s,
        phase: "open",
        sessionId: i.sessionId,
        startedAt: i.at,
        seconds: 0,
        lastActiveAt: i.at,
        idleAsk: false,
        error: null,
        closedNote: null,
      };
    case "server_state":
      if (i.state === "closed") {
        return {
          ...INITIAL_LINE,
          closedNote: closedNoteFor(i.seconds),
          noteAt: i.at,
          seconds: 0,
        };
      }
      if (i.state === "closing")
        return { ...s, phase: "closing", ...closingDrops };
      if (i.state === "open" && s.phase === "connecting")
        return {
          ...s,
          phase: "open",
          startedAt: s.startedAt ?? i.at,
          lastActiveAt: s.lastActiveAt ?? i.at,
        };
      return s;
    case "you":
      // The person speaking withdraws the question; Bash asking it does not.
      return {
        ...s,
        youOn: i.on,
        justDone: false,
        idleAsk: i.on ? false : s.idleAsk,
        lastActiveAt: i.on ? stamped(s, i.at) : s.lastActiveAt,
      };
    case "bash":
      return {
        ...s,
        bashOn: i.on,
        justDone: false,
        lastActiveAt: i.on ? stamped(s, i.at) : s.lastActiveAt,
      };
    case "delegation":
      return {
        ...s,
        step: "thinking",
        stepSeq: s.stepSeq + 1,
        justDone: false,
        ...noTask,
      };
    case "router": {
      const e = i.event;
      if (e.type === "tool_started")
        return {
          ...s,
          step: stepFor(e.tool, e.target),
          stepSeq: s.stepSeq + 1,
        };
      if (e.type === "action") {
        const a = e.action;
        // A task is born: the mark, and the spark's key.
        if (a.kind === "task_created" || a.kind === "task_followup")
          return {
            ...s,
            step: null,
            taskRunning: true,
            runningTaskId: a.taskId,
            births: s.births + 1,
          };
        return { ...s, step: null };
      }
      if (e.type === "error") return { ...s, step: null };
      if (e.type === "done")
        return { ...s, step: null, justDone: e.reason === "routed" };
      return s;
    }
    case "close":
      return s.phase === "off"
        ? s
        : { ...s, phase: "closing", ...closingDrops };
    case "failed":
      return { ...INITIAL_LINE, error: i.message, noteAt: i.at ?? null };
    case "reconnecting":
      return { ...s, reconnecting: i.on };
    case "tick": {
      if (s.phase !== "open" || s.startedAt === null) return s;
      const active = s.youOn || s.bashOn || s.step !== null;
      return {
        ...s,
        seconds: Math.max(0, Math.floor((i.at - s.startedAt) / 1000)),
        lastActiveAt: active ? i.at : s.lastActiveAt,
      };
    }
    case "task_settled":
      return s.taskRunning ? { ...s, ...noTask } : s;
    case "idle_ask":
      if (s.phase !== "open") return s;
      return s.idleAsk === i.on ? s : { ...s, idleAsk: i.on };
    default:
      // The union is exhaustive for the compiler; at runtime an input from
      // outside it must never write undefined into the store.
      return s;
  }
}

/** The server's ask, mirrored: 60 s with neither side speaking and no
 *  delegation in flight. Level, not edge: the hook asks every tick and
 *  dispatches idle_ask once the verdict flips on; only a you turns it off. */
export function idleMirror(s: LineSnapshot, now: number): boolean {
  return (
    s.phase === "open" &&
    !s.youOn &&
    !s.bashOn &&
    s.step === null &&
    s.lastActiveAt !== null &&
    now - s.lastActiveAt >= IDLE_ASK_MS
  );
}

const STEP_MOOD: Record<LineStep, FaceMood> = {
  thinking: "thinking",
  search: "browsing",
  read: "reading",
  create: "creating",
  nami: "recalling",
  other: "working",
};

export function faceMoodFor(s: LineSnapshot): FaceMood {
  if (s.phase === "off") return "present";
  if (s.phase === "connecting") return "waking";
  if (s.reconnecting) return "puzzled";
  if (s.phase === "closing" || s.idleAsk) return "waiting";
  // The person cutting in takes the face while the work continues. Bash
  // speaking keeps the same open eyes (the founder's ruling 2026-09-12: the
  // eyes never change shape for a spoken line) and talks through data-talk;
  // only a step in flight changes the eyes, thinking included.
  if (s.youOn || s.bashOn) return "listening";
  if (s.step) return STEP_MOOD[s.step];
  // A task running with no step in flight wears no face of its own: the run
  // mark is retired (the founder, 2026-09-13: tasks are many), so the line
  // rests until the next step or voice.
  return "listening";
}

export function toneFor(s: LineSnapshot): "none" | "gold" | "copper" {
  if (s.phase === "off") return "none";
  return s.bashOn ? "copper" : "gold";
}
